import { ReactNode } from 'react';
import { Tooltip, Typography, makeStyles } from '@material-ui/core';
import {
  Link,
  StatusAborted,
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
} from '@backstage/core-components';
import { SERVING_ENVS, servingEnvOf } from '../../lib/viva';
import { ArgoApp, appUrl, isHealthy } from './useArgoApps';

const useStyles = makeStyles(theme => ({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    whiteSpace: 'nowrap',
  },
  muted: {
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  },
}));

/** Индикатор по health-статусу ArgoCD; рассинхрон дописывается к подписи. */
export const HealthIndicator = ({
  health,
  sync,
  label,
}: {
  health: string;
  sync?: string;
  label?: string;
}) => {
  const classes = useStyles();
  const suffix = sync && sync !== 'Synced' ? ` · ${sync}` : '';
  const text = `${label ?? health}${suffix}`;
  const indicator = () => {
    switch (health) {
      case 'Healthy':
        return <StatusOK>{text}</StatusOK>;
      case 'Progressing':
        return <StatusPending>{text}</StatusPending>;
      case 'Degraded':
        return <StatusError>{text}</StatusError>;
      case 'Suspended':
        return <StatusAborted>{text}</StatusAborted>;
      default:
        return <StatusWarning>{text}</StatusWarning>;
    }
  };
  return <span className={classes.nowrap}>{indicator()}</span>;
};

const AppLink = ({ app, children }: { app: ArgoApp; children: ReactNode }) => {
  const url = appUrl(app);
  return url ? <Link to={url}>{children}</Link> : <>{children}</>;
};

/** Сводка по приложениям платформы в кластере: «3/4 Healthy» с расшифровкой в тултипе. */
export const ArgoAppsSummary = ({ apps }: { apps: ArgoApp[] }) => {
  const classes = useStyles();
  if (apps.length === 0) {
    return (
      <Typography variant="body2" className={classes.muted}>
        нет приложений
      </Typography>
    );
  }
  const healthy = apps.filter(isHealthy).length;
  let worst = 'Progressing';
  if (apps.some(app => app.health === 'Degraded')) {
    worst = 'Degraded';
  } else if (healthy === apps.length) {
    worst = 'Healthy';
  }
  const outOfSync = apps.filter(app => app.sync !== 'Synced').length;
  const details = (
    <div className={classes.stack}>
      {apps.map(app => (
        <span key={`${app.instance}/${app.name}`}>
          {app.name}: {app.health} · {app.sync}
        </span>
      ))}
    </div>
  );
  return (
    <Tooltip title={details}>
      <span>
        <HealthIndicator
          health={worst}
          label={`${healthy}/${apps.length} Healthy`}
          sync={outOfSync ? `${outOfSync} OutOfSync` : 'Synced'}
        />
      </span>
    </Tooltip>
  );
};

/**
 * Статус сервинга модели по окружениям (Application <модель>-staging / -production);
 * приложения вне этой схемы показываются под своими именами.
 */
export const ServingSummary = ({
  apps,
  modelName,
  envOf = servingEnvOf,
  rank = env => (env ? SERVING_ENVS.indexOf(env as never) : -1),
  empty = 'не развёрнута',
}: {
  apps: ArgoApp[];
  modelName: string;
  /** Окружение по имени Application; по умолчанию — схема моделей (staging/production). */
  envOf?: (appName: string, modelName: string) => string | undefined;
  /** Порядок окружений: меньше — выше; -1 для неизвестных. */
  rank?: (env: string | undefined) => number;
  empty?: string;
}) => {
  const classes = useStyles();
  if (apps.length === 0) {
    return (
      <Typography variant="body2" className={classes.muted}>
        {empty}
      </Typography>
    );
  }
  const order = (env: string | undefined) => {
    const r = rank(env);
    return r === -1 ? Number.MAX_SAFE_INTEGER : r;
  };
  const labelled = apps
    .map(app => ({ app, env: envOf(app.name, modelName) }))
    .sort((a, b) => order(a.env) - order(b.env));
  return (
    <div className={classes.stack}>
      {labelled.map(({ app, env }) => (
        <AppLink key={`${app.instance}/${app.name}`} app={app}>
          <HealthIndicator
            health={app.health}
            sync={app.sync}
            label={`${env ?? app.name}${
              app.instance ? ` @ ${app.instance}` : ''
            }: ${app.health}`}
          />
        </AppLink>
      ))}
    </div>
  );
};
